"use client";

import React, { useState, useEffect } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Card } from "@/components/ui/card";
import { useTranslation } from "next-i18next";
import { useSetRecoilState } from "recoil";
import { guideModalAtom } from "@/Atoms/modal/DialogueAtoms";
import { useRouter } from "next/router";
import PairsTradingApproachModal from "../marketplace/PairsTradingApproachModal";
import { useStrategyMode } from "@/contexts/StrategyModeContext";

export function SidebarCarousel() {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [approachModalOpen, setApproachModalOpen] = useState(false);

  const { t } = useTranslation("layout-page");
  const setGuideModal = useSetRecoilState(guideModalAtom);
  const router = useRouter();
  const { setMode } = useStrategyMode();

  const slides: {
    type?: "text" | "banner";
    title?: string;
    description?: React.ReactNode;
    bannerUrl?: string;
    linkUrl?: string;
    linkType?: "redirect" | "video";
    btnText?: string;
  }[] = [
    {
      type: "banner",
      bannerUrl: "/images/earnings-thumb.png",
      linkUrl: "https://www.youtube.com/embed/QaAfEObHFqk?si=askfxDqIgI6ID5ij",
      linkType: "video",
      title: "Earnings Strategy Guide",
      description:
        "Learn how to capture market movements during earnings season with automated flows.",
      btnText: "Watch Video",
    },
    {
      type: "banner",
      bannerUrl: "/images/pairs-thumb.png",
      linkUrl: "",
      title: "Automated Pairs Trading",
      description:
        "Discover powerful strategies designed to capture opportunities.",
      btnText: "Explore Strategies",
    },
    {
      type: "banner",
      bannerUrl: "/images/scanner-thumb.png",
      linkUrl: "/test?locale=en&tab=scanners",
      linkType: "redirect",
      title: "Market Scanner Bots",
      description:
        "Scan hundreds of stocks in real-time using custom indicator triggers.",
      btnText: "Launch Scanners",
    },
    {
      type: "banner",
      bannerUrl: "/images/predict-thumb.png",
      linkUrl: "/build?mode=prediction_market",
      linkType: "redirect",
      title: "Prediction Markets Alpha",
      description: "Deploy automated bots on event-based prediction markets.",
      btnText: "Build Prediction Bot",
    },
  ];
  // track current index and total count
  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };
    update();
    api.on("select", update);
    return () => {
      api.off("select", update);
    };
  }, [api]);

  // auto-slide every 3 seconds
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext(); // goes to next slide (loop enabled)
    }, 7000);
    return () => clearInterval(interval);
  }, [api]);

  const goTo = (idx: number) => {
    api?.scrollTo(idx);
  };

  return (
    <div className="relative w-full max-w-md mx-auto">
      <Carousel
        setApi={setApi}
        opts={{ loop: true, align: "center" }}
        className="w-full "
      >
        <CarouselContent>
          {slides.map((slide, idx) => (
            <CarouselItem
              key={idx}
              className="flex justify-center items-center"
            >
              <div className="p-1 w-full select-none">
                {slide.type === "banner" ? (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      if (slide.bannerUrl === "/images/pairs-thumb.png") {
                        setApproachModalOpen(true);
                      } else if (
                        slide.bannerUrl === "/images/predict-thumb.png"
                      ) {
                        setMode("prediction_market");
                        if (slide.linkUrl) {
                          router.push(slide.linkUrl);
                        }
                      } else if (slide.linkUrl) {
                        if (slide.linkType === "redirect") {
                          router.push(slide.linkUrl);
                        } else {
                          setGuideModal({ isOpen: true, link: slide.linkUrl });
                        }
                      }
                    }}
                    className="flex flex-col  overflow-hidden bg-card/60 hover:bg-card border border-border/80 hover:border-primary/50 rounded-2xl  text-left w-full cursor-pointer transition-all duration-300 group shadow-lg"
                  >
                    {/* Header Image Cover */}
                    <div className="relative w-full h-[120px]   overflow-hidden mb-3 bg-accent/40">
                      <img
                        src={slide.bannerUrl}
                        alt=""
                        className="w-full h-full object-fit  "
                      />
                    </div>
                    <div className="p-4">
                      {/* Subtitle/Description */}
                      <p className="text-[11px] text-muted-foreground leading-normal mb-3 px-0.5">
                        {slide.description}
                      </p>

                      {/* Action button */}
                      <div className="flex items-center justify-center w-full py-2 border border-border group-hover:border-primary/30 rounded-lg text-xs font-semibold text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-200">
                        {slide.btnText}
                        <span className="ml-1.5 transition-transform duration-200 group-hover:translate-x-1">
                          →
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Card className="bg-muted-foreground/15 h-[100px] p-2 text-sm flex flex-col gap-2 w-full pb-6">
                    <p className="font-bold">{slide.title}</p>
                    <p className="text-xs font-bold text-muted-foreground">
                      {slide.description}
                    </p>
                  </Card>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Dots navigation */}
      <div className="flex absolute bottom-2 left-4 justify-center space-x-2 hidden">
        {Array.from({ length: count }).map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`w-3 h-3 rounded-full ${
              idx === current ? "bg-muted-foreground" : "bg-muted-foreground/30"
            }`}
          />
        ))}
      </div>

      <PairsTradingApproachModal
        isOpen={approachModalOpen}
        onOpenChange={setApproachModalOpen}
      />
    </div>
  );
}
